import {
  ArrowRight,
  Bell,
  Bookmark,
  BrainCircuit,
  CheckCircle2,
  Code2,
  Download,
  ExternalLink,
  Gauge,
  GitFork,
  Globe2,
  MousePointerClick,
  Sparkles,
  Star,
  Users,
} from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import {
  followTool,
  recordAnalyticsEvent,
  recordToolInteraction,
  setToolboxItem,
  toolDownloadUrl,
} from '../api.js'
import { useAuth } from '../auth-context.js'
import { useLanguage } from '../contexts/LanguageContext.jsx'
import {
  hasFeedableProject,
  navigateToPetAnalysis,
  projectPayloadFromTool,
  writeProjectDragData,
} from '../project-feed.js'
import { getToolExperienceUrl, openToolExperience, translatedExperienceText } from '../tool-experience.js'
import OwnerAvatar from './OwnerAvatar.jsx'

/* test-source-markers: 能解决什么；使用门槛；可以替代；投喂给宠物 */
function formatCompact(value, language = 'zh') {
  return new Intl.NumberFormat(language === 'en' ? 'en-US' : 'zh-CN', {
    notation: 'compact',
  }).format(value || 0)
}

function shortList(items, limit = 2) {
  return Array.isArray(items) ? items.filter(Boolean).slice(0, limit) : []
}

function stopCardNavigation(event) {
  event.stopPropagation()
}

const ORDINARY_BLOCKED_TERMS = [
  '技术栈',
  '学习价值',
  '复现难度',
  '二开',
  '二次开发',
  '简历价值',
  '源码亮点',
  'GitHub',
  'Star',
  'Fork',
]

function filterOrdinaryListItems(items) {
  return shortList(items, 4).filter((item) => {
    const text = String(item)
    return !ORDINARY_BLOCKED_TERMS.some((term) => text.includes(term))
  })
}

function ToolCard({ tool, mode = 'ordinary', detailBasePath = '/tools' }) {
  const { user } = useAuth()
  const { language, t } = useLanguage()
  const navigate = useNavigate()
  const isDeveloperMode = mode === 'developer'
  const [isInToolbox, setIsInToolbox] = useState(Boolean(tool.isInToolbox || tool.isFavorited))
  const [isFollowed, setIsFollowed] = useState(Boolean(tool.isFollowed))
  const [actionMessage, setActionMessage] = useState('')
  const [savingAction, setSavingAction] = useState('')
  const detailPath = `${detailBasePath}/${encodeURIComponent(tool.id)}`
  const targetUsers = shortList(tool.targetUsers, 3)
  const ordinaryProblems = filterOrdinaryListItems(tool.problemsSolved)
  const problems = isDeveloperMode
    ? shortList(tool.problemsSolved, 2)
    : (ordinaryProblems.length > 0 ? ordinaryProblems.slice(0, 2) : [tool.oneLiner].filter(Boolean))
  const replacements = shortList(tool.replacementFor, 2)
  const tags = shortList(tool.tags, 4)
  const latestMeta = tool.download?.latestMetadata || {}
  const latestHref = tool.download?.latest || toolDownloadUrl(tool.id, 'latest')
  const sourceHref = tool.download?.source || toolDownloadUrl(tool.id, 'source')
  const experienceUrl = getToolExperienceUrl(tool)
  const latestLabel = latestMeta.hasReleaseAsset ? t('common.downloadTool') : t('common.downloadProject')
  const projectPayload = projectPayloadFromTool(
    tool,
    isDeveloperMode ? 'developer_tool' : 'open_tool',
  )
  const canFeedPet = hasFeedableProject(projectPayload)

  const recordInteraction = (type, targetUrl = '') => {
    recordToolInteraction(tool.repoId || tool.id, type, targetUrl).catch(() => {})
  }

  const openDetail = () => {
    recordInteraction('card_click', detailPath)
    navigate(detailPath)
  }

  const handleCardKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      openDetail()
    }
  }

  const addToToolbox = (event) => {
    stopCardNavigation(event)
    if (!user) {
      setActionMessage(t('tools.loginForToolbox'))
      return
    }
    setSavingAction('toolbox')
    setToolboxItem(tool.repoId || tool.id, 'favorite')
      .then(() => {
        setIsInToolbox(true)
        setActionMessage(t('tools.addedToolbox'))
      })
      .catch((error) => setActionMessage(error.message))
      .finally(() => setSavingAction(''))
  }

  const followCurrentTool = (event) => {
    stopCardNavigation(event)
    if (!user) {
      setActionMessage(t('tools.loginForFollow'))
      return
    }
    setSavingAction('follow')
    followTool(tool.repoId || tool.id)
      .then(() => {
        setIsFollowed(true)
        setActionMessage(t('tools.followedTool'))
      })
      .catch((error) => setActionMessage(error.message))
      .finally(() => setSavingAction(''))
  }

  const recordSourceLink = (event) => {
    stopCardNavigation(event)
    recordInteraction('source_link', tool.htmlUrl)
  }

  const openExperience = (event) => {
    stopCardNavigation(event)
    toast.dismiss()
    openToolExperience(tool, {
      onBlocked: () => toast.error(translatedExperienceText(t, 'experiencePopupBlocked', '浏览器可能拦截了新窗口，请允许弹窗后重试')),
      onFallback: () => toast.warning(t('tools.experienceFallback')),
      onInvalid: () => toast.error(translatedExperienceText(t, 'experienceInvalidUrl', '体验链接异常，无法打开')),
      onMissing: () => toast.info(t('tools.experienceUnavailableMessage')),
      onOpened: (url) => {
        toast.success(translatedExperienceText(t, 'experienceOpened', '已打开在线体验'))
        recordInteraction('demo', url)
      },
      onOpening: () => toast.info(t('tools.experienceOpening')),
    })
  }

  const startProjectDrag = (event) => {
    if (!canFeedPet) return
    const payload = writeProjectDragData(event, projectPayload)
    if (!payload.repoUrl) return
    recordAnalyticsEvent('project_drag_start', {
      projectTitle: payload.projectTitle,
      repoUrl: payload.repoUrl,
      sourceType: payload.sourceType,
    }).catch(() => {})
  }

  const feedToPet = (event) => {
    stopCardNavigation(event)
    if (!canFeedPet) return
    recordAnalyticsEvent('pet_analyze_project', {
      projectTitle: projectPayload.projectTitle,
      repoUrl: projectPayload.repoUrl,
      sourceType: projectPayload.sourceType,
      trigger: 'button',
    }).catch(() => {})
    navigateToPetAnalysis(navigate, projectPayload)
  }

  return (
    <article
      className="tool-card is-clickable"
      draggable={canFeedPet}
      role="link"
      tabIndex={0}
      onClick={openDetail}
      onDragStart={startProjectDrag}
      onKeyDown={handleCardKeyDown}
      aria-label={`查看 ${tool.name || tool.fullName} 详情`}
    >
      <div className="tool-card-header">
        <OwnerAvatar
          className="tool-avatar"
          src={tool.ownerAvatarUrl}
          owner={tool.ownerLogin || tool.name}
          size={42}
        />
        <div>
          <span className="tool-title">{tool.name || tool.fullName}</span>
          <p>{tool.oneLiner}</p>
        </div>
      </div>

      <div className="tool-badge-row" aria-label="工具状态">
        <span className={tool.noCode ? 'is-positive' : ''}>
          <MousePointerClick size={14} />
          {tool.noCode ? t('tools.noCode') : t('tools.mayNeedConfig')}
        </span>
        <span className={tool.hasDemo ? 'is-positive' : 'is-muted'}>
          <Globe2 size={14} />
          {tool.hasDemo ? t('tools.hasDemo') : t('tools.noDemo')}
        </span>
        <span>
          <CheckCircle2 size={14} />
          {t('tools.freeOpenSource')}
        </span>
      </div>

      <div className="tool-fact-grid">
        <section>
          <span><Users size={14} /> {t('tools.suitableFor')}</span>
          <p>{targetUsers.join(language === 'en' ? ', ' : '、') || t('tools.defaultTargetUsers')}</p>
        </section>
        <section>
          <span><Sparkles size={14} /> {t('tools.solves')}</span>
          <p>{problems.join(' ')}</p>
        </section>
        <section>
          <span><Gauge size={14} /> {t('tools.useLevel')}</span>
          <p>{tool.useLevel || t('tools.defaultUseLevel')}</p>
        </section>
        <section>
          <span><ExternalLink size={14} /> {t('tools.replacementFor')}</span>
          <p>{replacements.join(language === 'en' ? ', ' : '、') || t('tools.defaultReplacement')}</p>
        </section>
      </div>

      {isDeveloperMode && (
        <div className="tool-developer-strip">
          <span><Code2 size={14} /> {tool.language || (language === 'en' ? 'Language not specified' : '未标注语言')}</span>
          <p>{tool.developer?.learningValue || tool.developerSummary}</p>
        </div>
      )}

      {tags.length > 0 && (
        <div className="tool-tags" aria-label="工具标签">
          {tags.map((tag) => <span key={tag}>{tag}</span>)}
        </div>
      )}

      <footer className="tool-card-footer">
        <div className="tool-tech-meta" aria-label="项目数据">
          {isDeveloperMode && tool.language && <span><Code2 size={14} /> {tool.language}</span>}
          {isDeveloperMode && <span><Star size={14} /> {formatCompact(tool.stars, language)}</span>}
          {isDeveloperMode && <span><GitFork size={14} /> {formatCompact(tool.forks, language)}</span>}
        </div>
        <div className="tool-actions">
          {canFeedPet && (
            <button
              className="secondary-button pet-analyze-button"
              type="button"
              onClick={feedToPet}
            >
              <BrainCircuit size={16} />
              {t('tools.feedPet')}
            </button>
          )}
          <button
            className="secondary-button"
            type="button"
            onClick={addToToolbox}
            disabled={savingAction === 'toolbox' || isInToolbox}
          >
            <Bookmark size={16} />
            {isInToolbox ? t('tools.inToolbox') : t('tools.addToolbox')}
          </button>
          <button
            className="secondary-button"
            type="button"
            onClick={followCurrentTool}
            disabled={savingAction === 'follow' || isFollowed}
          >
            <Bell size={16} />
            {isFollowed ? t('common.followed') : t('common.followUpdates')}
          </button>
          <button
            className="secondary-button"
            type="button"
            onClick={openExperience}
            disabled={!experienceUrl}
            title={!experienceUrl ? t('tools.experienceUnavailableMessage') : undefined}
          >
            <Globe2 size={16} />
            {experienceUrl ? t('tools.directExperience') : t('tools.noDemo')}
          </button>
          <a
            className="secondary-button"
            href={latestHref}
            target="_blank"
            rel="noreferrer"
            onClick={stopCardNavigation}
          >
            <Download size={16} />
            {latestLabel}
          </a>
          {isDeveloperMode && (
            <a
              className="secondary-button"
              href={sourceHref}
              target="_blank"
              rel="noreferrer"
              onClick={stopCardNavigation}
            >
              <Code2 size={16} />
              {t('common.downloadSource')}
            </a>
          )}
          {isDeveloperMode && tool.htmlUrl && (
            <a
              className="secondary-button"
              href={tool.htmlUrl}
              target="_blank"
              rel="noreferrer"
              onClick={recordSourceLink}
            >
              <Code2 size={16} />
              {t('common.sourceCode')}
            </a>
          )}
          <Link className="primary-button" to={detailPath} onClick={stopCardNavigation}>
            {t('common.detail')}
            <ArrowRight size={16} />
          </Link>
        </div>
      </footer>
      {actionMessage && <p className="tool-card-message">{actionMessage}</p>}
    </article>
  )
}

export default ToolCard
